import { DownloadIcon, PencilAltIcon } from '@heroicons/react/outline';
import katex from 'katex';
import React, {
  FC,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { CodeProps } from 'react-markdown/lib/ast-to-react';
import { ReactMarkdownProps } from 'react-markdown/lib/complex-types';
import remarkGfm from 'remark-gfm';

import 'katex/dist/katex.min.css';

import {
  linkifyTimestamps,
  secondsFromTimestampHref,
} from '@/lib/transcriptChapters';

import { useSandboxChatId } from '@/contexts/SandboxChatContext';

import ChatTable from './ChatTable';
import { CodeBlock } from './CodeBlock';
// import EditModal from '../images/EditModal';

interface Props {
  message: string;
  onImageEdit?: (prompt: string, newImageUrl: string) => void;
  searchResults?: boolean;
  preferLinkText?: boolean;
  /**
   * Text about a recording: its times ("12:30") become buttons that call
   * this with the seconds. Pass a function that never changes (the block is
   * memoized on the message).
   */
  onTimestampClick?: (seconds: number) => void;
  /** The recording's length: later "times" stay text (clock times). */
  timestampMaxSeconds?: number | null;
}

const TimestampButton = ({
  seconds,
  onClick,
  children,
}: {
  seconds: number;
  onClick: (seconds: number) => void;
  children: ReactNode;
}) => (
  <button
    type='button'
    onClick={(event) => {
      event.preventDefault();
      onClick(seconds);
    }}
    className='mx-px inline-flex items-baseline rounded-md bg-primary-50 px-1.5 font-medium tabular-nums text-primary-700 ring-1 ring-inset ring-primary-100 transition-colors hover:bg-primary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/20 dark:hover:bg-primary-500/20'
  >
    {children}
  </button>
);

interface ImageWithDownloadProps {
  src?: string;
  alt?: string;
  onImageEdit?: (prompt: string, newImageUrl: string) => void;
}

interface UpdatedImage {
  image_url?: string;
}

// Memoize static components
const CustomList = memo(({ children }: { children: ReactNode }) => (
  <ul className='list-outside list-disc space-y-2 pl-5 marker:text-primary-600'>
    {processMathInNode(children)}
  </ul>
));
CustomList.displayName = 'CustomList';

// Numbered lists keep their numbers (and a list that starts at 3 starts at 3).
const CustomOrderedList = memo(
  ({ children, start }: { children: ReactNode; start?: number }) => (
    <ol
      start={start}
      className='list-outside list-decimal space-y-2 pl-5 marker:text-primary-600'
    >
      {processMathInNode(children)}
    </ol>
  ),
);
CustomOrderedList.displayName = 'CustomOrderedList';

// A div, not a p: items of a loose list already arrive wrapped in <p>, and a
// <p> inside a <p> is invalid HTML that breaks hydration.
const CustomListItem = memo(({ children }: { children: ReactNode }) => (
  <li>
    <div className='my-0'>{processMathInNode(children)}</div>
  </li>
));
CustomListItem.displayName = 'CustomListItem';

// Media URL detection helpers
const AUDIO_EXTENSIONS = [
  '.mp3',
  '.wav',
  '.ogg',
  '.aac',
  '.m4a',
  '.opus',
  '.flac',
  '.webm',
];
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];

const isMediaUrl = (url: string, extensions: string[]): boolean => {
  if (!url || !url.startsWith('http')) return false;
  const cleanUrl = url.toLowerCase().split('?')[0].split('#')[0];
  return extensions.some((ext) => cleanUrl.endsWith(ext));
};

const isAudioUrl = (url: string): boolean => isMediaUrl(url, AUDIO_EXTENSIONS);
const isVideoUrl = (url: string): boolean => isMediaUrl(url, VIDEO_EXTENSIONS);

// Inline audio player for markdown links
const InlineAudioPlayer = memo<{ src: string; title?: string }>(
  ({ src, title }) => (
    <div className='my-2 w-full max-w-lg'>
      <audio
        controls
        src={src}
        className='w-full rounded-lg'
        preload='metadata'
      />
      {title && (
        <div className='mt-1 flex items-center justify-between text-xs text-gray-400 dark:text-gray-500'>
          <span className='truncate'>{title}</span>
          <a
            href={src}
            download
            className='ml-2 flex-shrink-0 text-primary-600 hover:underline'
          >
            Download
          </a>
        </div>
      )}
    </div>
  ),
);
InlineAudioPlayer.displayName = 'InlineAudioPlayer';

// Inline video player for markdown links
const InlineVideoPlayer = memo<{ src: string; title?: string }>(
  ({ src, title }) => (
    <div className='my-2 w-full max-w-2xl'>
      <video
        controls
        src={src}
        className='max-h-[70vh] w-auto max-w-full rounded-lg'
        preload='metadata'
      />
      {title && (
        <div className='mt-1 flex items-center justify-between text-xs text-gray-400 dark:text-gray-500'>
          <span className='truncate'>{title}</span>
          <a
            href={src}
            download
            className='ml-2 flex-shrink-0 text-primary-600 hover:underline'
          >
            Download
          </a>
        </div>
      )}
    </div>
  ),
);
InlineVideoPlayer.displayName = 'InlineVideoPlayer';

const CustomLink = memo<
  ReactMarkdownProps & {
    href?: string;
    searchResults?: boolean;
    preferLinkText?: boolean;
  }
>(({ href, children, searchResults, preferLinkText }) => {
  const getFaviconUrl = useCallback((url: string) => {
    try {
      const hostname = new URL(url || '').hostname;
      return `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`;
    } catch {
      return '/images/globe-icon.png';
    }
  }, []);

  const getHostname = useCallback((url: string) => {
    try {
      return new URL(url || '').hostname;
    } catch {
      return url;
    }
  }, []);

  const getFaviconUrlFromTitle = useCallback(
    (title: string, fallbackUrl: string) => {
      const normalizedTitle = title.trim();
      const titleDomain = normalizedTitle.match(
        /(?:https?:\/\/)?(?:www\.)?([a-z0-9-]+(?:\.[a-z0-9-]+)+)/i,
      )?.[1];

      if (titleDomain) {
        return `https://www.google.com/s2/favicons?domain=${titleDomain}&sz=64`;
      }

      return getFaviconUrl(fallbackUrl);
    },
    [getFaviconUrl],
  );

  const isUrl = useCallback((text: string) => {
    return text.startsWith('http');
  }, []);

  const childrenText =
    typeof children === 'string' ? children : String(children || '');
  const hostname = getHostname(href || '');
  const childIsUrl = isUrl(childrenText);

  // Truncate long URL text for display (must be before early return to follow hooks rules)
  const displayText = useMemo(() => {
    if (childIsUrl && childrenText.length > 60) {
      try {
        const url = new URL(childrenText);
        const path = url.pathname + url.search;
        const truncatedPath =
          path.length > 30 ? path.slice(0, 30) + '...' : path;
        return url.hostname + truncatedPath;
      } catch {
        return childrenText.slice(0, 60) + '...';
      }
    }
    return children;
  }, [childIsUrl, childrenText, children]);

  // Render audio URLs as inline players
  if (href && isAudioUrl(href)) {
    const title = !childIsUrl ? childrenText : undefined;
    return <InlineAudioPlayer src={href} title={title} />;
  }

  // Render video URLs as inline players
  if (href && isVideoUrl(href)) {
    const title = !childIsUrl ? childrenText : undefined;
    return <InlineVideoPlayer src={href} title={title} />;
  }

  if (preferLinkText && !childIsUrl && childrenText) {
    return (
      <a
        href={href}
        className='group relative ml-1 inline-flex max-w-full items-center gap-2 rounded-xl bg-[#f4f4f4] px-2 py-0.5 text-[0.85em] font-medium text-gray-700 hover:bg-gray-700 hover:text-white dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-gray-600'
        target='_blank'
        rel='noopener noreferrer'
        title={childrenText}
      >
        <img
          src={getFaviconUrlFromTitle(childrenText, href || '')}
          width={12}
          height={12}
          alt=''
          className='h-3 w-3 shrink-0'
        />
        <span className='truncate'>{children}</span>
      </a>
    );
  }

  // if link text (children) is a url, show the link as first option with icon
  if (
    !childIsUrl &&
    !href?.includes(childrenText) &&
    !href?.includes('easy-peasy.ai')
  ) {
    return (
      <a
        href={href}
        className='ml-1 inline-flex h-[22px] items-center rounded-xl bg-[#f4f4f4] px-2 text-[0.5em] font-medium text-gray-600 hover:bg-gray-700 hover:text-white dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-gray-600 group relative'
        target='_blank'
        rel='noopener noreferrer'
      >
        <span className='truncate uppercase flex items-center gap-2'>
          <img
            src={getFaviconUrl(href || '')}
            width={12}
            height={12}
            alt={hostname}
          />
          {hostname}
        </span>
        <div className='absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-700 text-white text-[10px] whitespace-normal leading-4 rounded p-2 shadow-lg z-10 max-w-xs'>
          <div className='flex items-center'>
            <img
              src={getFaviconUrl(href || '')}
              width={16}
              height={16}
              alt={hostname}
              className='inline mr-2'
            />
            {children}
          </div>
        </div>
      </a>
    );
  }

  return (
    <a
      href={href}
      className='text-primary-600 underline hover:no-underline'
      target='_blank'
      rel='noreferrer'
      title={childIsUrl && childrenText.length > 60 ? childrenText : undefined}
    >
      {displayText}
    </a>
  );
});
CustomLink.displayName = 'CustomLink';

// Separate the image download logic
const downloadImage = async (src: string): Promise<void> => {
  try {
    const response = await fetch(src);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `image.${src.split('.').pop() || 'png'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading image:', error);
    window.open(src, '_blank');
  }
};

const ImageWithDownload = memo<ImageWithDownloadProps>(
  ({ src, alt, onImageEdit }) => {
    // const [showEditModal, setShowEditModal] = useState(false);

    const handleDownload = useCallback(
      async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (src) {
          await downloadImage(src);
        }
      },
      [src],
    );

    // const handleUpdate = useCallback(
    //   (updatedImage: UpdatedImage, prompt: string) => {
    //     if (onImageEdit && updatedImage.image_url) {
    //       onImageEdit(prompt, updatedImage.image_url);
    //     }
    //     setShowEditModal(false);
    //   },
    //   [onImageEdit]
    // );

    // Spans (not divs): react-markdown renders inline images inside <p>, and a
    // block element there is invalid HTML that fires a hydration error on
    // every page embedding markdown (chat, FAQs). display comes from classes.
    return (
      <span className='relative block w-full'>
        <img
          src={src}
          alt={alt}
          className='h-auto max-h-[1100px] max-w-full rounded-lg'
        />
        {/* <EditModal
          image={{ image_url: src }}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onUpdate={handleUpdate}
        /> */}
        {src && (
          <span className='absolute right-2 top-2 flex space-x-2'>
            <a
              href={`/ai-image-editor?imageUrl=${encodeURIComponent(src)}`}
              target='_blank'
              rel='noreferrer'
              className='rounded-full bg-white/80 p-2 shadow-sm hover:bg-white'
              title='Edit image'
            >
              <PencilAltIcon className='h-5 w-5 text-gray-700' />
            </a>
            <a
              href={src}
              download={`image.${src.split('.').pop() || 'png'}`}
              className='rounded-full bg-white/80 p-2 shadow-sm hover:bg-white'
              title='Download image'
              onClick={handleDownload}
            >
              <DownloadIcon className='h-5 w-5 text-gray-700' />
            </a>
          </span>
        )}
      </span>
    );
  },
);
ImageWithDownload.displayName = 'ImageWithDownload';

// Models often embed SANDBOX paths in markdown (![chart](/home/user/x.png)) —
// especially less capable ones that skip uploading to public storage — and the
// exact shape varies by model: full path, ~/chart.png, ./chart.png or a bare
// chart.png (relative to the sandbox home dir, where all tools write). Resolve
// every non-URL src to a sandbox path and fetch a presigned URL for it.
const NON_SANDBOX_SRC_RE = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

const toSandboxPath = (src: string | undefined): string | null => {
  if (!src || NON_SANDBOX_SRC_RE.test(src)) return null;
  if (src.startsWith('/')) return src;
  return `/home/user/${src.replace(/^(?:\.\/|~\/)/, '')}`;
};

const SandboxAwareImage = memo<ImageWithDownloadProps>(
  ({ src, alt, onImageEdit }) => {
    const chatId = useSandboxChatId();
    const sandboxPath = toSandboxPath(src);
    const isSandboxPath = sandboxPath !== null;
    const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
      if (!sandboxPath || !chatId) return;
      let cancelled = false;
      fetch('/api/sandbox/files/download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, path: sandboxPath }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (cancelled) return;
          if (!data?.url) {
            setFailed(true);
            return;
          }
          // The presign endpoint doesn't check existence — probe the URL so a
          // deleted file (expired sandbox) shows the placeholder, not a broken img.
          const probe = new window.Image();
          probe.onload = () => {
            if (!cancelled) setResolvedUrl(data.url);
          };
          probe.onerror = () => {
            if (!cancelled) setFailed(true);
          };
          probe.src = data.url;
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
      return () => {
        cancelled = true;
      };
    }, [sandboxPath, chatId]);

    if (isSandboxPath && !resolvedUrl) {
      if (failed || !chatId) {
        return (
          <span className='my-1 inline-block rounded-md bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400'>
            🖼 {alt || 'Image'}: {src}
            {failed ? ' (file no longer available)' : ''}
          </span>
        );
      }
      return (
        <span className='my-2 block aspect-video w-full max-w-md animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700' />
      );
    }

    return (
      <ImageWithDownload
        src={resolvedUrl ?? src}
        alt={alt}
        onImageEdit={onImageEdit}
      />
    );
  },
);
SandboxAwareImage.displayName = 'SandboxAwareImage';

// Format the content for LaTeX rendering
const formatForLatex = (content: string) => {
  // If it's a LaTeX environment, don't touch it
  if (/\\begin\{.*?\}/.test(content) || /\\end\{.*?\}/.test(content)) {
    return content;
  }
  // Remove any trailing \n and replace all \n with space
  content = content.replace(/\\n/g, ' ').trim();
  // Handle division formatting: convert (2x) / 2 to \frac{2x}{2}
  content = content.replace(/\((.*?)\)\s*\/\s*(\d+)/g, '\\frac{$1}{$2}');
  // Handle basic division with / sign
  content = content.replace(/(\w+)\s*\/\s*(\w+)/g, '\\frac{$1}{$2}');
  // Handle subtraction with proper spacing
  content = content.replace(/(\w+)\s*−\s*(\w+)/g, '$1 - $2');
  // Add proper spacing around operations
  content = content.replace(/(\w+)([+\-=])(\w+)/g, '$1 $2 $3');
  return content;
};

// Helper to decide if content should be display or inline math
const isDisplayMath = (content: string) => {
  // If content is only math (no implication, no text, no commas, no colons, no arrows, no words except math variables)
  // e.g. "2r + 82 = 132" or "x = 2"
  // If it contains implication arrows or is part of a chain, treat as inline
  // If it contains only math symbols and spaces, treat as display
  // If it contains ",", ":", "⇒", "->", or words, treat as inline
  // If it is surrounded by spaces or is the only thing in the brackets, treat as display
  const trimmed = content.trim();
  if (/[,;:⇒→\-\>]/.test(trimmed)) return false;
  if (
    /\s/.test(trimmed) &&
    /[a-zA-Z]/.test(trimmed) &&
    !/\s/.test(trimmed.replace(/[^a-zA-Z]/g, ''))
  )
    return false;
  // Only math symbols and numbers
  if (/^[\d\s=+\-*/^()rxt]+$/.test(trimmed)) return true;
  // If it's a single equation
  if (/^\s*[a-zA-Z0-9_]+\s*[=><]+\s*[a-zA-Z0-9_\s+\-*/^()]+\s*$/.test(trimmed))
    return true;
  return false;
};

// Helper to decide if content should be rendered as math
const isMathContent = (content: string) => {
  // Contains math symbols, numbers, or LaTeX commands or environments
  if (/\\begin\{.*?\}/.test(content) || /\\end\{.*?\}/.test(content))
    return true;
  return /[=+\-*/^_0-9\\]|\\begin|\\frac|\\sqrt|\\sum|\\int|\\left|\\right|\\cdot|\\times|\\leq|\\geq|\\neq|\\approx|\\infty|\\pi|\\theta|\\alpha|\\beta|\\gamma|\\Delta|\\forall|\\exists|\\rightarrow|\\leftarrow|\\Rightarrow|\\Leftarrow|\\pm|\\div|\\over|\\underline|\\bar|\\hat|\\vec|\\dot|\\ddot|\\ldots|\\cdots|\\dots|\\text|\\displaystyle/.test(
    content,
  );
};

const processMathString = (text: string) => {
  // Regex for custom LaTeX delimiter: <<| ... |>> (multi-line) and &lt;&lt;| ... |&gt;&gt; (escaped)
  const regex = /<<\|(.*?)\|>>|&lt;&lt;\|(.*?)\|&gt;&gt;/gs;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const [fullMatch, unescaped, escaped] = match;
    const content = unescaped ?? escaped;
    const offset = match.index;
    // Push preceding text
    if (lastIndex < offset) {
      parts.push(text.slice(lastIndex, offset));
    }
    // Render as math using KaTeX directly (avoids react-latex-next
    // delimiter parsing which conflicts with \$ in content)
    const formattedContent = formatForLatex(content);
    try {
      const html = katex.renderToString(formattedContent.trim(), {
        displayMode: /\n/.test(content),
        throwOnError: false,
      });
      parts.push(
        <span
          key={offset + '-math'}
          dangerouslySetInnerHTML={{ __html: html }}
        />,
      );
    } catch {
      parts.push(formattedContent);
    }
    lastIndex = offset + fullMatch.length;
  }
  // Push any remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length === 1 ? parts[0] : parts;
};

// Helper to process any ReactNode (string, <br>, array, etc)
const processMathInNode = (node: ReactNode): ReactNode => {
  if (typeof node === 'string') {
    return processMathString(node);
  }
  if (Array.isArray(node)) {
    return node.map((child, i) => (
      <React.Fragment key={i}>{processMathInNode(child)}</React.Fragment>
    ));
  }
  // Recurse into inline formatting elements (bold, italic, etc.)
  // so <<|...|>> inside **bold** or *italic* is still processed
  if (React.isValidElement(node)) {
    const type = node.type;
    if (
      type === 'strong' ||
      type === 'em' ||
      type === 'b' ||
      type === 'i' ||
      type === 'del'
    ) {
      return React.cloneElement(
        node,
        {},
        processMathInNode((node.props as any).children),
      );
    }
  }
  return node;
};

// Pre-process <<|...|>> math delimiters before markdown parsing:
// 1. Escape | in delimiters so GFM tables don't split them as columns
// 2. Double backslashes inside so markdown doesn't consume LaTeX escapes
//    (e.g. \$ → \\$ → markdown unescapes to \$ → KaTeX renders as $)
// `text` is typed as string but reaches us from callers that pass optional
// fields (e.g. an absent `request_body.keywords`), so guard rather than throw.
const escapeMathDelimiters = (text: string): string =>
  (text ?? '').replace(/<<\|([\s\S]*?)\|>>/g, (_match, content: string) => {
    const escaped = content.replace(/\\/g, '\\\\');
    return '<<\\|' + escaped + '\\|>>';
  });

const MarkdownBlock: FC<Props & { inline?: boolean }> = memo(
  ({
    message,
    onImageEdit,
    searchResults,
    preferLinkText = false,
    inline = false,
    onTimestampClick,
    timestampMaxSeconds,
  }) => {
    const components = useMemo(
      () => ({
        code({ node, inline, className, children, ...props }: CodeProps) {
          const match = /language-(\w+)/.exec(className || '');
          return !inline && match ? (
            <CodeBlock
              language={match[1]}
              value={String(children).replace(/\n$/, '')}
              {...props}
            />
          ) : (
            <code className={className} {...props}>
              {children}
            </code>
          );
        },
        img: ({ src, alt }: { src?: string; alt?: string }) => (
          <SandboxAwareImage src={src} alt={alt} onImageEdit={onImageEdit} />
        ),
        ul: CustomList,
        ol: CustomOrderedList,
        li: CustomListItem,
        a: (props: ReactMarkdownProps & { href?: string }) => {
          const seconds = onTimestampClick
            ? secondsFromTimestampHref(props.href)
            : null;
          if (seconds !== null && onTimestampClick) {
            return (
              <TimestampButton seconds={seconds} onClick={onTimestampClick}>
                {props.children}
              </TimestampButton>
            );
          }
          return (
            <CustomLink
              {...props}
              searchResults={searchResults}
              preferLinkText={preferLinkText}
            />
          );
        },
        p: ({ children }: { children: ReactNode }) => (
          <p>{processMathInNode(children)}</p>
        ),
        // Tables scroll sideways in their own container (wide tables stay
        // readable on phones) and get Copy, Download and full-screen actions.
        table: ({ children }: { children: ReactNode }) => (
          <ChatTable>{children}</ChatTable>
        ),
        td: ({ children }: { children: ReactNode }) => (
          <td>{processMathInNode(children)}</td>
        ),
        th: ({ children }: { children: ReactNode }) => (
          <th>{processMathInNode(children)}</th>
        ),
        span: ({ children }: { children: ReactNode }) => (
          <span>{processMathInNode(children)}</span>
        ),
      }),
      [onImageEdit, searchResults, preferLinkText, onTimestampClick],
    );

    const markdown = useMemo(
      () =>
        onTimestampClick
          ? linkifyTimestamps(message, { maxSeconds: timestampMaxSeconds })
          : message,
      [message, onTimestampClick, timestampMaxSeconds],
    );

    // Pre-process the message if inline mode is true
    const processedMessage = useMemo(() => {
      if (inline && typeof message === 'string' && /\[.*?\]/g.test(message)) {
        const processed = message.replace(/\[(.*?)\]/g, (match, content) => {
          if (/^(Problem|Analysis|Solution|Answer)$/i.test(content.trim())) {
            return match;
          }
          const formattedContent = formatForLatex(content);
          const isEquation = content.includes('=');
          if (isEquation) {
            return `$$${formattedContent}$$`;
          } else {
            return `$${formattedContent}$`;
          }
        });
        return processed;
      }
      return message;
    }, [message, inline]);

    return inline ? (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components as any}
        unwrapDisallowed
        disallowedElements={['p', 'div']}
      >
        {escapeMathDelimiters(
          typeof processedMessage === 'string' ? processedMessage : message,
        )}
      </ReactMarkdown>
    ) : (
      <div className='marky-markdown'>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={components as any}
        >
          {escapeMathDelimiters(markdown)}
        </ReactMarkdown>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.message === nextProps.message &&
      prevProps.preferLinkText === nextProps.preferLinkText &&
      prevProps.onTimestampClick === nextProps.onTimestampClick &&
      prevProps.timestampMaxSeconds === nextProps.timestampMaxSeconds
    );
  },
);

MarkdownBlock.displayName = 'MarkdownBlock';

export default MarkdownBlock;
